import { osnovaApi } from '../base';
import { parseResponse } from '../parseResponse';
import { parseFilenameFromDisposition } from '@shared/lib/downloadFile';
import type {
  KfoBinding,
  KfoControlKind,
  KfoFacets,
  KfoFieldSuggestions,
  KfoGuarantee,
  KfoGuaranteePage,
  KfoImport,
  KfoImportPreview,
  KfoLineDetail,
  KfoLinePage,
  KfoReconcile,
  KfoReport,
  KfoReportDetail,
  KfoReportKind,
  KfoRfiPage,
  KfoRfiSource,
  KfoRfiStatus,
  KfoSummary,
  KfoSummaryGroup,
  KfoValidation,
} from '../types';

// ОСНОВА.Инженер — раздел «КФО» (контроль физических объёмов Заказчика),
// docs/pto-kfo-spec.md §5/§11. Строк в срезе ~20 тыс. — реестр, журнал RFI и
// ГО всегда листаются на сервере (page/per_page), целиком не грузятся.

/**
 * Узкие теги отчётов: автосохранение объёма (`PUT …/items`) меняет только
 * число позиций в списке и сверку — перечитывать из-за него полную карточку
 * отчёта (её сохранятор берёт из ответа PUT), открытые карточки строк и т.д.
 * незачем: для отчёта на тысячи позиций это мегабайты на каждую пачку ввода.
 * Общий тег `'KfoReports'` по-прежнему сбрасывает всё.
 */
const REPORTS_LIST = { type: 'KfoReports' as const, id: 'LIST' };
const RECONCILE = { type: 'KfoReports' as const, id: 'RECONCILE' };

/** Серверные фильтры реестра строк КФО (§5). Пустые значения не уходят в запрос. */
export interface KfoLinesArgs {
  projectId: string;
  q?: string;
  zone?: string;
  titul?: string;
  mark?: string;
  work_kind?: string;
  contract_no?: string;
  rd_sheet_code?: string;
  ep_code?: string;
  responsible?: string;
  status?: string;
  /** Только строки, которых нет в последнем срезе. */
  absent?: boolean;
  /** Черновик отчёта: сервер вернёт его объём по строке в `draft_qty`. */
  report_id?: string;
  /** Номер страницы с 1. */
  page?: number;
  per_page?: number;
}

export interface KfoRfiArgs {
  projectId: string;
  control_kind?: KfoControlKind | '';
  source?: KfoRfiSource | '';
  status?: KfoRfiStatus | '';
  /** Поиск по № RFI / № RFI ПО / Ключу. */
  q?: string;
  line_id?: string;
  page?: number;
  per_page?: number;
}

export interface KfoGuaranteesArgs {
  projectId: string;
  source?: KfoRfiSource | '';
  /** Только просроченные. */
  overdue?: boolean;
  /** Поиск по № письма / Ключу. */
  q?: string;
  page?: number;
  per_page?: number;
}

type QueryValue = string | number | boolean | null | undefined;

/** Строка запроса без пустых значений (`''`, `null`, `undefined`, `false`). */
function qs(params: Record<string, QueryValue>): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '' || v === false) continue;
    sp.set(k, String(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : '';
}

/** Список из `{items: […]}` или голого массива — терпим обе формы ответа. */
function listOf<T>(raw: unknown): T[] {
  if (Array.isArray(raw)) return raw as T[];
  const items = (raw as { items?: unknown } | null)?.items;
  return Array.isArray(items) ? (items as T[]) : [];
}

/** Отчёт из `KfoReport` или обёртки `{report: …}` (создание черновика). */
function reportOf(raw: unknown): KfoReport {
  const r = raw as { report?: KfoReport } | KfoReport;
  return 'report' in r && r.report ? r.report : (r as KfoReport);
}

/**
 * Файл печатной формы. Успешный ответ — `File` с кириллическим именем из
 * `Content-Disposition` (RFC 5987), если сервер его прислал, иначе `Blob`.
 * Ошибка (404/422) разбирается как JSON, чтобы UI показал причину, а не
 * «[object Blob]».
 */
async function fileResponse(response: Response): Promise<unknown> {
  if (!response.ok) return parseResponse(response);
  const blob = await response.blob();
  const name = parseFilenameFromDisposition(response.headers.get('content-disposition'));
  return name ? new File([blob], name, { type: blob.type }) : blob;
}

export const ptoKfoApi = osnovaApi.injectEndpoints({
  endpoints: (b) => ({
    // ── Импорт среза и привязки договоров ──
    /** Разбор файла среза без записи: договоры, число строк, текущие привязки. */
    previewKfoImport: b.mutation<KfoImportPreview, FormData>({
      query: (form) => ({ url: '/agents/pto/kfo/imports/preview', method: 'POST', body: form }),
    }),
    /**
     * Загрузка среза: multipart `file` + `bindings` (JSON `[{contract_no,
     * project_id | null}]` — все договоры предпросмотра, `null` = «не загружать
     * в этот раз») + `force` (повтор после 409 `older_slice`), §12.
     */
    applyKfoImport: b.mutation<KfoImport, { form: FormData; force?: boolean }>({
      query: ({ form, force }) => ({
        url: `/agents/pto/kfo/imports${force ? '?force=true' : ''}`,
        method: 'POST',
        body: form,
      }),
      invalidatesTags: [
        'KfoImports',
        'KfoBindings',
        'KfoLines',
        'KfoRfi',
        'KfoGuarantees',
        'KfoReports',
        // Срез закрывает наши ГО — вместе с ними закрываются сроки в календаре.
        'Deadline',
      ],
    }),
    listKfoImports: b.query<KfoImport[], void>({
      query: () => '/agents/pto/kfo/imports',
      transformResponse: (raw: unknown) => listOf<KfoImport>(raw),
      providesTags: ['KfoImports'],
    }),
    listKfoBindings: b.query<KfoBinding[], void>({
      query: () => '/agents/pto/kfo/bindings',
      transformResponse: (raw: unknown) => listOf<KfoBinding>(raw),
      providesTags: ['KfoBindings'],
    }),
    deleteKfoBinding: b.mutation<unknown, string>({
      query: (id) => ({ url: `/agents/pto/kfo/bindings/${id}`, method: 'DELETE' }),
      invalidatesTags: ['KfoBindings'],
    }),

    // ── Реестр строк ──
    getKfoLines: b.query<KfoLinePage, KfoLinesArgs>({
      query: ({ projectId, ...f }) => `/agents/pto/projects/${projectId}/kfo/lines${qs({ ...f })}`,
      providesTags: ['KfoLines'],
    }),
    /** Карточка строки: строка + RFI + ГО + изменения по срезам + позиции наших отчётов. */
    getKfoLine: b.query<KfoLineDetail, { projectId: string; lineId: string }>({
      query: ({ projectId, lineId }) => `/agents/pto/projects/${projectId}/kfo/lines/${lineId}`,
      providesTags: (_r, _e, a) => [
        { type: 'KfoLines', id: a.lineId },
        'KfoReports',
        'KfoRfi',
        'KfoGuarantees',
      ],
    }),
    /** Привязка строки КФО к позиции объёма РД (`position_id`) и наше примечание (`note`). */
    patchKfoLine: b.mutation<
      unknown,
      {
        projectId: string;
        lineId: string;
        position_id?: string | null;
        note?: string | null;
      }
    >({
      query: ({ projectId, lineId, ...body }) => ({
        url: `/agents/pto/projects/${projectId}/kfo/lines/${lineId}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_r, _e, a) => [{ type: 'KfoLines', id: a.lineId }, 'KfoLines'],
    }),
    getKfoFacets: b.query<KfoFacets, string>({
      query: (projectId) => `/agents/pto/projects/${projectId}/kfo/facets`,
      providesTags: ['KfoLines'],
    }),

    // ── Отчёты СМР / Приложения к RFI ──
    listKfoReports: b.query<KfoReport[], string>({
      query: (projectId) => `/agents/pto/projects/${projectId}/kfo/reports`,
      transformResponse: (raw: unknown) => listOf<KfoReport>(raw),
      providesTags: [REPORTS_LIST],
    }),
    createKfoReport: b.mutation<
      KfoReport,
      { projectId: string; kind: KfoReportKind; report_date: string; number?: string; note?: string }
    >({
      query: ({ projectId, ...body }) => ({
        url: `/agents/pto/projects/${projectId}/kfo/reports`,
        method: 'POST',
        body,
      }),
      transformResponse: (raw: unknown) => reportOf(raw),
      // Новый черновик — сразу в закэшированный список, не дожидаясь перечитывания:
      // иначе экран, выбравший его по `onCreated`, не нашёл бы id в старом списке
      // и сбросил бы выбор как «черновик удалён».
      async onQueryStarted({ projectId }, { dispatch, queryFulfilled }) {
        try {
          const { data: created } = await queryFulfilled;
          dispatch(
            ptoKfoApi.util.updateQueryData('listKfoReports', projectId, (draft) => {
              if (!draft.some((r) => r.id === created.id)) draft.unshift(created);
            }),
          );
        } catch {
          /* ошибку показывает вызывающий экран */
        }
      },
      invalidatesTags: [REPORTS_LIST],
    }),
    getKfoReport: b.query<KfoReportDetail, string>({
      query: (id) => `/agents/pto/kfo/reports/${id}`,
      providesTags: (_r, _e, id) => [{ type: 'KfoReports', id }],
    }),
    /** Удалить черновик (soft). Выпущенный отчёт сервер не удалит. */
    deleteKfoReport: b.mutation<unknown, string>({
      query: (id) => ({ url: `/agents/pto/kfo/reports/${id}`, method: 'DELETE' }),
      invalidatesTags: ['KfoReports', 'KfoLines'],
    }),
    /** Upsert позиций черновика; `qty ≤ 0` удаляет позицию. */
    putKfoReportItems: b.mutation<
      KfoReportDetail,
      { reportId: string; items: { line_id: string; qty: number }[] }
    >({
      query: ({ reportId, items }) => ({
        url: `/agents/pto/kfo/reports/${reportId}/items`,
        method: 'PUT',
        body: items,
      }),
      // Карточку отчёта и `draft_qty` страниц реестра сохранятор обновляет из
      // ответа (useQtySaver) — здесь только список (число позиций), сверка и
      // карточки затронутых строк (позиции наших отчётов в них).
      invalidatesTags: (_r, _e, { items }) => [
        REPORTS_LIST,
        RECONCILE,
        ...items.map((i) => ({ type: 'KfoLines' as const, id: i.line_id })),
      ],
    }),
    validateKfoReport: b.mutation<KfoValidation, string>({
      query: (id) => ({ url: `/agents/pto/kfo/reports/${id}/validate`, method: 'POST' }),
    }),
    /** Выпуск: снимок значений; при ошибках проверки — 422 с `KfoValidation`. */
    issueKfoReport: b.mutation<KfoReportDetail, string>({
      query: (id) => ({ url: `/agents/pto/kfo/reports/${id}/issue`, method: 'POST' }),
      invalidatesTags: ['KfoReports', 'KfoLines', 'KfoRfi'],
    }),
    /** Печатная форма Заказчика (XLSX). Лениво, по клику. */
    exportKfoReportXlsx: b.query<Blob, string>({
      query: (id) => ({
        url: `/agents/pto/kfo/reports/${id}/xlsx`,
        cache: 'no-store',
        responseHandler: fileResponse,
      }),
    }),
    /** Подсказки объёма из принятых фактов Прораба за период отчёта (этап 2). */
    getKfoFieldSuggestions: b.query<KfoFieldSuggestions, string>({
      query: (id) => `/agents/pto/kfo/reports/${id}/field-suggestions`,
      keepUnusedDataFor: 0,
    }),
    applyKfoFieldSuggestions: b.mutation<
      KfoReportDetail,
      { reportId: string; items: { line_id: string; qty: number; fact_ids: string[] }[] }
    >({
      query: ({ reportId, items }) => ({
        url: `/agents/pto/kfo/reports/${reportId}/field-suggestions/apply`,
        method: 'POST',
        body: items,
      }),
      invalidatesTags: ['KfoReports', 'KfoLines'],
    }),

    // ── Журнал RFI ──
    getKfoRfi: b.query<KfoRfiPage, KfoRfiArgs>({
      query: ({ projectId, ...f }) => `/agents/pto/projects/${projectId}/kfo/rfi${qs({ ...f })}`,
      providesTags: ['KfoRfi'],
    }),
    /** Связи RFI с заявкой на освидетельствование / актом ИД / исполнительной гео-схемой; статус — только у наших. */
    patchKfoRfi: b.mutation<
      unknown,
      {
        id: string;
        inspection_request_id?: string | null;
        executive_act_id?: string | null;
        geodesy_scheme_id?: string | null;
        status?: KfoRfiStatus;
      }
    >({
      query: ({ id, ...body }) => ({ url: `/agents/pto/kfo/rfi/${id}`, method: 'PATCH', body }),
      invalidatesTags: ['KfoRfi'],
    }),

    // ── Гарантийные обязательства ──
    getKfoGuarantees: b.query<KfoGuaranteePage, KfoGuaranteesArgs>({
      query: ({ projectId, ...f }) =>
        `/agents/pto/projects/${projectId}/kfo/guarantees${qs({ ...f })}`,
      providesTags: ['KfoGuarantees'],
    }),
    /** Выпуск гарантийного письма по выбранным строкам (этап 3). */
    createKfoGuaranteeLetter: b.mutation<
      KfoGuarantee[],
      {
        projectId: string;
        letter_no: string;
        letter_date: string;
        due_date: string;
        items: { line_id: string; qty: number }[];
      }
    >({
      query: ({ projectId, ...body }) => ({
        url: `/agents/pto/projects/${projectId}/kfo/guarantees`,
        method: 'POST',
        body,
      }),
      transformResponse: (raw: unknown) => listOf<KfoGuarantee>(raw),
      // Сервер ставит срок ГО в календарь (`deadlines`, kind kfo_go).
      invalidatesTags: ['KfoGuarantees', 'Deadline'],
    }),
    /** DOCX гарантийного письма (этап 3). Лениво, по клику. */
    exportKfoGuaranteeLetterDocx: b.query<Blob, { projectId: string; letterNo: string }>({
      query: ({ projectId, letterNo }) => ({
        url: `/agents/pto/kfo/guarantee-letters/docx${qs({
          project_id: projectId,
          letter_no: letterNo,
        })}`,
        cache: 'no-store',
        responseHandler: fileResponse,
      }),
    }),

    // ── Сводка и сверка (этап 2) ──
    getKfoSummary: b.query<KfoSummary, { projectId: string; group: KfoSummaryGroup }>({
      query: ({ projectId, group }) =>
        `/agents/pto/projects/${projectId}/kfo/summary${qs({ group })}`,
      providesTags: ['KfoLines', 'KfoGuarantees', 'KfoRfi'],
    }),
    getKfoReconcile: b.query<KfoReconcile, string>({
      query: (projectId) => `/agents/pto/projects/${projectId}/kfo/reconcile`,
      providesTags: ['KfoLines', 'KfoReports', RECONCILE],
    }),
  }),
  overrideExisting: false,
});

export const {
  usePreviewKfoImportMutation,
  useApplyKfoImportMutation,
  useListKfoImportsQuery,
  useListKfoBindingsQuery,
  useDeleteKfoBindingMutation,
  useGetKfoLinesQuery,
  useGetKfoLineQuery,
  usePatchKfoLineMutation,
  useGetKfoFacetsQuery,
  useListKfoReportsQuery,
  useCreateKfoReportMutation,
  useGetKfoReportQuery,
  useDeleteKfoReportMutation,
  usePutKfoReportItemsMutation,
  useValidateKfoReportMutation,
  useIssueKfoReportMutation,
  useLazyExportKfoReportXlsxQuery,
  useGetKfoFieldSuggestionsQuery,
  useApplyKfoFieldSuggestionsMutation,
  useGetKfoRfiQuery,
  usePatchKfoRfiMutation,
  useGetKfoGuaranteesQuery,
  useCreateKfoGuaranteeLetterMutation,
  useLazyExportKfoGuaranteeLetterDocxQuery,
  useGetKfoSummaryQuery,
  useGetKfoReconcileQuery,
} = ptoKfoApi;
